import { Link } from "react-router-dom";
import { MapPin, ShieldCheck, ChevronRight, ArrowRight } from "lucide-react";
import { Seo, PHONE, EMAIL } from "../components/Seo";
import { CtaBand } from "../components/CtaBand";
import { SERVICES } from "../data/services";
import { cityBySlug, cityPath, comboPath } from "../data/cities";

export default function CityPage({ city }) {
  const nearby = (city.nearby || []).map(cityBySlug).filter(Boolean);

  const faqs = [
    {
      q: `Do you provide licensed security guards in ${city.name}?`,
      a: `Yes. 1st Nations Security provides fully licensed, uniformed security guards in ${city.name} and across ${city.region}. All guards hold valid Ontario security licences under the PSISA and are backed by our 24/7 dispatch centre.`,
    },
    {
      q: `What security services are available in ${city.name}?`,
      a: `In ${city.name} we provide uniformed security guards, mobile patrol security, construction site security, event security and crowd management, retail loss prevention, concierge security, industrial security, healthcare security, 24/7 fire watch, alarm response, executive protection, Indigenous community security, K9 security and airport/transit security.`,
    },
    {
      q: `How do I hire security guards in ${city.name}, Ontario?`,
      a: `Call +1 807-770-0243 or email admin@1stnation-security.com. Tell us your ${city.name} location, site type and coverage hours and our dispatch team will build a security plan for you.`,
    },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div data-testid={`city-page-${city.slug}`} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-8">
      <Seo
        title={`Security Guard Services ${city.name} ON | 1st Nations Security`}
        description={`Security guard services in ${city.name}, Ontario. Licensed guards, mobile patrol, construction & event security, fire watch & more in ${city.name}. 24/7: +1 807-770-0243.`}
        path={`/security-guards-${city.slug}-ontario`}
        jsonLd={jsonLd}
      />
      <nav data-testid="city-breadcrumb" className="text-xs text-slate-500 mb-6 flex items-center gap-1.5">
        <Link to="/" className="hover:text-amber-300">Home</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to="/ontario-service-areas" className="hover:text-amber-300">Ontario Service Areas</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-slate-300">{city.name}</span>
      </nav>

      <p className="text-xs font-semibold uppercase tracking-widest text-amber-500 mb-3 flex items-center gap-2">
        <MapPin className="h-3.5 w-3.5" /> {city.name}, Ontario — {city.region}
      </p>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-white font-heading mb-6">
        Security Guard Services in {city.name}, Ontario
      </h1>
      <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-6">{city.intro}</p>
      <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-10">
        Whether you need a uniformed security guard for your {city.name} storefront, overnight construction site
        security, mobile patrol for multiple properties, fire watch coverage or event security and crowd management,
        our licensed Ontario security guards are available in {city.name} 24 hours a day, 7 days a week. As a 100%
        Indigenous-owned security company, we bring community-first values and professional standards to every
        {" "}{city.name} assignment.
      </p>

      <section data-testid="city-services-section" className="my-12">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-4">
          Our Security Services in {city.name}
        </h2>
        <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-6">
          Every 1st Nations Security service is available in {city.name} and throughout {city.region}:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((s) => (
            <Link
              key={s.slug}
              to={comboPath(city, s)}
              data-testid={`city-service-link-${s.slug}`}
              className="group rounded-xl border border-white/10 bg-slate-900/60 p-5 transition-all duration-200 hover:border-amber-500/40 hover:bg-slate-900"
            >
              <s.icon className="h-6 w-6 text-amber-400 mb-3" />
              <h3 className="text-base font-semibold text-white mb-1.5 group-hover:text-amber-300 transition-colors">
                {s.name} in {city.name}
              </h3>
              <p className="text-xs text-slate-400 leading-relaxed">{s.short}</p>
            </Link>
          ))}
        </div>
      </section>

      <section data-testid="city-industries-section" className="my-12">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-6">
          {city.name} Industries We Protect
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl">
          {city.industries.map((ind) => (
            <div key={ind} className="flex items-start gap-3 rounded-xl border border-white/10 bg-slate-900/60 px-5 py-4">
              <ShieldCheck className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
              <span className="text-sm text-slate-200">{ind} in {city.name}</span>
            </div>
          ))}
        </div>
      </section>

      {nearby.length > 0 && (
        <section data-testid="city-nearby-section" className="my-12">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-4">
            Security Services Near {city.name}
          </h2>
          <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-5">
            We also provide security guard services in communities near {city.name}:
          </p>
          <div className="flex flex-wrap gap-3">
            {nearby.map((n) => (
              <Link
                key={n.slug}
                to={cityPath(n)}
                data-testid={`city-nearby-link-${n.slug}`}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-slate-950/70 px-4 py-2 text-sm text-slate-300 transition-colors hover:border-amber-500/40 hover:text-amber-300"
              >
                <MapPin className="h-3.5 w-3.5 text-amber-500" /> Security Guards {n.name}
              </Link>
            ))}
            <Link
              to="/ontario-service-areas"
              data-testid="city-all-areas-link"
              className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 px-4 py-2 text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-500/10"
            >
              All Ontario Service Areas <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </section>
      )}

      <section data-testid="city-faq-section" className="my-12">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-6">
          {city.name} Security — FAQs
        </h2>
        <div className="space-y-4 max-w-3xl">
          {faqs.map((f, i) => (
            <details key={i} data-testid={`city-faq-${i}`} className="group rounded-xl border border-white/10 bg-slate-900/60 p-5">
              <summary className="cursor-pointer text-base font-semibold text-white list-none flex items-center justify-between gap-3">
                {f.q}
                <ChevronRight className="h-4 w-4 text-amber-400 shrink-0 transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <CtaBand
        heading={`Hire Security Guards in ${city.name}`}
        sub={`Call our 24/7 dispatch at ${PHONE} or email ${EMAIL} for security guard services in ${city.name}, Ontario — uniformed guards, mobile patrol, event security, fire watch and more.`}
      />
    </div>
  );
}
